import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOrders } from "@/context/OrdersContext";
import { formatTimeAgo } from "@/pages/branch/orders/constants";
import type { Order } from "@/mocks/branch/orders";

const BANNER_DURATION = 5000;
const MAX_BANNERS = 3;

export default function GlobalAlerts() {
  const {
    orders,
    unreadNumbers,
    toasts,
    connection,
    openOrderDetail,
    dismissToast,
  } = useOrders();
  const navigate = useNavigate();
  const [banners, setBanners] = useState<number[]>([]);
  const shownRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const fresh = unreadNumbers.filter((n) => !shownRef.current.has(n));
    if (fresh.length === 0) return;
    fresh.forEach((n) => shownRef.current.add(n));
    setBanners((prev) => [...fresh.slice().reverse(), ...prev].slice(0, MAX_BANNERS));
    fresh.forEach((n) => {
      window.setTimeout(() => {
        setBanners((prev) => prev.filter((b) => b !== n));
      }, BANNER_DURATION);
    });
  }, [unreadNumbers]);

  const bannerOrders = banners
    .map((n) => orders.find((o) => o.number === n))
    .filter((o): o is Order => Boolean(o));

  const handleView = (id: string) => {
    openOrderDetail(id);
    navigate("/orders");
  };

  return (
    <>
      {connection === "lost" ? (
        <div className="fixed top-0 inset-x-0 z-[60] bg-red-600 text-white flex items-center justify-center gap-2.5 h-11 px-4 animate-fade-slide-down">
          <i className="ri-loader-4-line animate-spin text-lg"></i>
          <span className="text-sm font-semibold">انقطع الاتصال — جاري إعادة المحاولة</span>
        </div>
      ) : null}

      {bannerOrders.length > 0 ? (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] pointer-events-none">
          <div className="space-y-2 pointer-events-auto">
            {bannerOrders.map((order) => (
              <div
                key={order.id}
                className="flex items-center gap-3 bg-accent-500 text-foreground-900 rounded-card px-4 py-3 shadow-dropdown animate-fade-slide-down"
              >
                <div className="w-9 h-9 rounded-full bg-foreground-900 text-accent-500 flex items-center justify-center shrink-0">
                  <i className="ri-notification-3-line text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold whitespace-nowrap truncate">
                    طلب جديد #{order.number}
                  </p>
                  <p className="text-xs font-medium text-foreground-700/80">
                    {formatTimeAgo(order.minutesAgo)} · {order.total.toFixed(2)} ر.س
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleView(order.id)}
                  className="h-11 px-4 rounded-btn bg-foreground-900 text-accent-500 text-sm font-bold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  عرض
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="fixed bottom-6 left-6 z-[60] flex flex-col gap-2">
        {toasts.map((toast) => (
          <button
            key={toast.id}
            type="button"
            onClick={() => dismissToast(toast.id)}
            className={`flex items-center gap-2.5 min-w-[260px] max-w-sm px-4 py-3 rounded-card shadow-dropdown text-sm font-semibold text-right cursor-pointer animate-fade-slide-up ${
              toast.type === "success"
                ? "bg-primary-500 text-white"
                : toast.type === "error"
                  ? "bg-red-500 text-white"
                  : "bg-foreground-900 text-white"
            }`}
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i
                className={`${
                  toast.type === "success"
                    ? "ri-checkbox-circle-line"
                    : toast.type === "error"
                      ? "ri-error-warning-line"
                      : "ri-information-line"
                } text-lg`}
              ></i>
            </div>
            <span className="flex-1 leading-snug">{toast.message}</span>
          </button>
        ))}
      </div>
    </>
  );
}